import React, { Component } from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { formValueSelector, SubmissionError } from 'redux-form';
import { single } from 'validate.js';
import get from 'lodash/get';

import {
  userExists, unauthenticateUser, authenticateUserWithPassword, sendMagicLink,
} from '../../actions/users';
import { connectWithFacebook } from '../../actions/users';
import { submitSinglePayerPayment } from '../../actions/purchases';
import { tokenizeCard } from '../../lib/card-functions';
import { ACTIVE } from '../../lib/payment-statuses';
import { formatMessage } from '../../lib/intl';

import SinglePayerPayment from './single-payer-payment';
import { SinglePayerPaymentFormName } from './single-payer-payment-form';
import { i18n } from './single-payer-payment.i18n';

class SinglePayerPaymentContainer extends Component {
  formatMessage = formatMessage(this.props.intl ? this.props.intl : {});

  state = {
    submitting: false,
    alerts: [],
  }

  componentDidUpdate(prevProps) {
    const { emailEntered: previousEmail } = prevProps;
    const { emailEntered, userExists } = this.props;
    const isValidEmail = !single(emailEntered, { email: true, presence: true });
    const emailChanged = previousEmail !== emailEntered;
    // Length condition is to reset accountType when we start typing a new email. Weird but works.
    if (emailChanged && (isValidEmail || !emailEntered || emailEntered.length < 2)) {
      userExists(emailEntered);
    }
  }

  get redirectUrl() {
    const { purchase: { successReturnUrl, slug } } = this.props;
    const confirmationUrl = `/purchases/${slug}/single-payer-payment-successful`;
    if (successReturnUrl) return successReturnUrl;
    return `${confirmationUrl}${ this.paymentWasSent ? '?alreadyPaid=true' : ''}`;
  }

  get paymentWasSent() {
    const { purchase: { currentPayment } } = this.props;
    return !currentPayment || currentPayment.status !== ACTIVE;
  }

  onSubmit = (values, something) => {
    const { user, purchase, accountType } = this.props;
    const email = get(user, 'email', values.email);
    const password = accountType === 'invitee' ? get(values, 'guestPassword.password') : get(values, 'password');
    this.setState({ submitting: true });
    this._formValues = {
      auth: {
        email,
        password,
        createGuestAccount: accountType === 'invitee' ? get(values, 'guestPassword.guest', false) : false,
      },
      allowedShareAmount: purchase.currentTotalCostAmount,
      // hardcoding empty fees, will use projection soon.
      allowedFees: { consumerFee: { amountCents: 0, currencyCode: purchase.currencyCode } },
      card: {
        ...values.card,
        platformId: purchase.paymentPlatform.id,
      },
    };
    if (values.card.paymentSourceId && values.card.paymentSourceId !== 'new') {
      return this.performSubmitCommit(this._formValues);
    }

    // When tokenize card finishes, the cardTokenized callback on SinglePayerPayment is called
    // See also card-fields.js
    tokenizeCard(values.card, email, purchase.paymentPlatform);
  }

  onAuthenticate = (values) => {
    const { authenticateUserWithPassword } = this.props;
    const { email, password } = values;
    return authenticateUserWithPassword(email, password)
      .catch((error) => {
        if (error.is401) {
          throw new SubmissionError({
            password: this.formatMessage(i18n.invalidPassword),
            _error: 'Login failed!'
          });
        } else {
          throw error;
        }
      });
  }

  onCardTokenized = (cardToken) => {
    const values = {
      ...this._formValues,
      card: {
        ...this._formValues.card,
        paymentSource: cardToken,
      },
    }

    return this.performSubmitCommit(values);
  }

  onCardError = () => {
    this.setState({ submitting: false });
  }

  onSendLink = () => {
    const { emailEntered } = this.props;
    const { alerts } = this.state;
    return this.props.sendMagicLink(emailEntered)
      .then(() => this.setState({
        alerts: [...alerts,  {
          type: 'success',
          title: this.formatMessage(i18n.linkSentAlertTitle),
          text: this.formatMessage(i18n.linkSentAlertBody)
        }]
      }));
  }

  performSubmitCommit(values) {
    const { purchase, authentication } = this.props;
    const { alerts } = this.state;
    return this.props.submitSinglePayerPayment(purchase, values, authentication.token)
      .then(() => {
        this.setState({ submitting: true });
        this.redirectToConfirmation();
      })
      .catch((error) => {
        const errorText = error.message === 'no_pwd' ? this.formatMessage(i18n.invalidPassword) :
          error.message;
        this.setState({
          submitting: false,
          alerts: [...alerts, { type: 'error', title: '', text: errorText }]
        });
      });
  }

  redirectToConfirmation() {
    window.location.href = this.redirectUrl;
  }

  render() {
    const { purchase, emailEntered, user, accountType, initialValues } = this.props;
    const { submitting, alerts } = this.state;
    if (this.paymentWasSent) this.redirectToConfirmation();

    return (
      <SinglePayerPayment
        emailEntered={emailEntered}
        purchase={purchase}
        user={user}
        accountType={accountType}
        initialValues={initialValues}
        submitDisabled={submitting}
        onCardTokenized={this.onCardTokenized}
        onCardError={this.onCardError}
        onSubmitPayment={this.onSubmit}
        onLogin={this.onAuthenticate}
        onLogout={this.props.unauthenticateUser}
        connectWithFacebook={this.props.connectWithFacebook}
        onSendLink={this.onSendLink}
        alerts={alerts}
      />
    );
  }
};

const mapStateToProps = (state) => {
  const selector = formValueSelector(SinglePayerPaymentFormName);
  const emailEntered = selector(state, 'email');
  return {
    emailEntered,
    purchase: state.purchases.byId[state.purchases.current],
    user: state.users.byId[state.users.current],
    authentication: state.session.authentication,
    accountType: state.users.existence || 'invitee',
    initialValues: buildInitialValues(state),
  };
};

const buildInitialValues = (state) => {
  const purchase = state.purchases.byId[state.purchases.current];
  const organizerEmail = get(purchase, 'group.organizerEmail');
  const selector = formValueSelector(SinglePayerPaymentFormName);
  const card = selector(state, 'card');
  const emailEntered = selector(state, 'email');
  const passwordEntered = selector(state, 'password');
  const signIn = selector(state, 'signIn');
  const isSignin = selector(state, '__justSignIn');
  const signInValues = {};

  if (isSignin) {
    signInValues.signIn = {
      email: emailEntered,
      ...signIn,
    };
  }

  return {
    card,
    signIn,
    email: emailEntered || organizerEmail,
    password: passwordEntered,
    guestPassword: {
      guest: !state.users.existence
    },
    __justSignIn: isSignin,
    ...signInValues,
  };
};

const mapDispatchToProps = {
  userExists,
  unauthenticateUser,
  submitSinglePayerPayment,
  authenticateUserWithPassword,
  connectWithFacebook,
  sendMagicLink,
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(SinglePayerPaymentContainer));



// WEBPACK FOOTER //
// ./src/desktop/pages/single-payer-payment/single-payer-payment.container.js